import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ClockIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline'
import { enquiryAPI } from '../../services/api'
import toast from 'react-hot-toast'

const Contact = () => {
    const [isSubmitted, setIsSubmitted] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm()

    const enquiryMutation = useMutation({
        mutationFn: enquiryAPI.createEnquiry,
        onSuccess: (data) => {
            toast.success('Message sent successfully! We will get back to you soon.')
            reset()
            setIsSubmitted(true)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send message')
        },
    })

    const onSubmit = (data) => {
        const enquiryData = {
            ...data,
            source: 'contact_page',
            consent_terms: 'true',
        }

        enquiryMutation.mutate(enquiryData)
    }

    return (
        <>
            <Helmet>
                <title>Contact Us | MyMargdarshak</title>
                <meta name="description" content="Get in touch with MyMargdarshak for education guidance, course information, and admission support. We're here to help you achieve your career goals." />
                <meta name="keywords" content="contact, education guidance, admission help, career counseling, support" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 flex flex-col pt-0">
                {/* Modern Premium Light Theme Contact Hero Section */}
                <section className="relative pt-28 lg:pt-40 pb-20 lg:pb-32 overflow-hidden bg-white border-b border-gray-100">
                    {/* Dynamic Mesh Gradient Background - Subtle for Light Theme */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-accent-100 blur-[120px] animate-pulse"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-primary-100 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                    </div>

                    {/* Subtle Overlay Grid */}
                    <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="inline-flex items-center space-x-2 bg-accent-50 border border-accent-100 px-4 py-2 rounded-full mb-8"
                            >
                                <span className="w-2 h-2 bg-accent-500 rounded-full animate-ping"></span>
                                <span className="text-sm font-black text-accent-700 uppercase tracking-widest">Connect With Us</span>
                            </motion.div>

                            <motion.h1 
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                className="text-5xl sm:text-7xl lg:text-8xl font-black text-slate-900 leading-[1.1] tracking-[-0.04em] mb-8"
                            >
                                Let's Start Your <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-600 to-primary-600">
                                    Success Story
                                </span>
                            </motion.h1>

                            <motion.p 
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed font-medium"
                            >
                                Have questions about courses, admissions, or career paths? Our expert counselors are here to guide you every step of the way.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Contact Form and Info */}
                <section className="py-16">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            {/* Contact Form */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                className="card p-8"
                            >
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>

                                {isSubmitted ? (
                                    <div className="text-center py-8">
                                        <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent Successfully!</h3>
                                        <p className="text-gray-600 mb-6">
                                            Thank you for contacting us. We will get back to you within 24 hours.
                                        </p>
                                        <button
                                            onClick={() => setIsSubmitted(false)}
                                            className="btn-primary"
                                        >
                                            Send Another Message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('name', { required: 'Name is required' })}
                                                    className="input"
                                                    placeholder="Enter your full name"
                                                />
                                                {errors.name && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email *
                                                </label>
                                                <input
                                                    type="email"
                                                    {...register('email', {
                                                        required: 'Email is required',
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: 'Invalid email address'
                                                        }
                                                    })}
                                                    className="input"
                                                    placeholder="Enter your email"
                                                />
                                                {errors.email && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    {...register('phone', {
                                                        required: 'Phone number is required',
                                                        pattern: {
                                                            value: /^[\+]?[1-9][\d]{0,15}$/,
                                                            message: 'Invalid phone number'
                                                        }
                                                    })}
                                                    className="input"
                                                    placeholder="Enter your phone number"
                                                />
                                                {errors.phone && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('city', { required: 'City is required' })}
                                                    className="input"
                                                    placeholder="Enter your city"
                                                />
                                                {errors.city && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Subject
                                            </label>
                                            <select
                                                {...register('subject')}
                                                className="input"
                                            >
                                                <option value="">Select a subject</option>
                                                <option value="course_inquiry">Course Inquiry</option>
                                                <option value="admission_help">Admission Help</option>
                                                <option value="career_guidance">Career Guidance</option>
                                                <option value="technical_support">Technical Support</option>
                                                <option value="general">General</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Message *
                                            </label>
                                            <textarea
                                                {...register('message', { required: 'Message is required' })}
                                                rows={7}
                                                className="input"
                                                placeholder="Tell us how we can help you..."
                                            />
                                            {errors.message && (
                                                <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                                            )}
                                        </div>

                                        <div className="flex items-start space-x-3">
                                            <input
                                                type="checkbox"
                                                id="consent_marketing"
                                                {...register('consent_marketing')}
                                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="consent_marketing" className="text-sm text-gray-600">
                                                I agree to receive updates about courses and educational opportunities via email and WhatsApp
                                            </label>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={enquiryMutation.isPending}
                                            className="btn-primary btn-lg w-full"
                                        >
                                            {enquiryMutation.isPending ? (
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Sending...</span>
                                                </div>
                                            ) : (
                                                'Send Message'
                                            )}
                                        </button>
                                    </form>
                                )}
                            </motion.div>

                            {/* Contact Information */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="space-y-8"
                            >
                                {/* Contact Details */}
                                <div className="card p-8">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>

                                    <div className="space-y-6">
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <PhoneIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                                                <p className="text-gray-600">+91-9810530354</p>
                                                <p className="text-sm text-gray-500">Mon-Sat, 9AM-6PM</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <EnvelopeIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                                                <p className="text-gray-600">admission@mymargdarshak.com</p>
                                                <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <MapPinIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                                                <p className="text-gray-600">
                                                    Lal Kuah, Ghaziabad<br />
                                                    Uttar Pradesh, India<br />
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <ClockIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                                                <p className="text-gray-600">
                                                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                                                    Saturday: 10:00 AM - 4:00 PM<br />
                                                    Sunday: Closed
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                {/* FAQ Link */}
                                <div className="card p-8">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Have Questions?</h2>
                                    <p className="text-gray-600 mb-4">
                                        Check out our frequently asked questions for quick answers to common queries.
                                    </p>
                                    <a
                                        href="/faqs"
                                        className="btn-outline w-full"
                                    >
                                        View FAQs
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-24 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Quick Answers</h2>
                            <p className="text-lg text-gray-500 max-w-2xl mx-auto font-medium">Common questions students ask us before getting started.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {[
                                {
                                    q: "How can I apply for admission?",
                                    a: "You can apply directly through our website by filling out the enquiry form. Our counselors will then guide you through the specific university's application process."
                                },
                                {
                                    q: "Are the courses UGC recognized?",
                                    a: "Yes, we only partner with universities that are UGC and DEB recognized, ensuring your degree is globally valid and accepted for government jobs."
                                },
                                {
                                    q: "What documents are required?",
                                    a: "Generally, you'll need your 10th and 12th marksheets, a government-issued ID proof, and passport-size photographs. PG courses require graduation certificates."
                                },
                                {
                                    q: "Do you offer placement support?",
                                    a: "Many of our partner universities have dedicated placement cells. Additionally, Margdarshak provides career guidance to help you land your dream job."
                                }
                            ].map((faq, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="bg-white p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-300"
                                >
                                    <h3 className="text-lg font-black text-gray-900 mb-3">{faq.q}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">{faq.a}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Map Section */}
                <section className="h-[450px] relative mt-16 grayscale hover:grayscale-0 transition-all duration-700">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.954627447432!2d77.44755107623199!3d28.63110298414418!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cee2285555555%3A0x1c8b3d686b2455ed!2sGhaziabad%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1710500000000!5m2!1sen!2sin"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Our Location"
                    ></iframe>
                </section>
            </div>
        </>
    )
}

export default Contact
